import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, LogOut, ChevronDown, UserCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';

const AuthStatus: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { user, signOut } = useAuth();
  const [initials, setInitials] = useState('');

  useEffect(() => {
    if (user?.email) {
      // Extract initials from email (first letter before @ and first letter of domain)
      const parts = user.email.split('@');
      if (parts.length === 2) {
        const firstInitial = parts[0][0]?.toUpperCase() || '';
        const secondInitial = parts[1][0]?.toUpperCase() || '';
        setInitials(`${firstInitial}${secondInitial}`);
      }
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      setIsOpen(false);
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) {
    return (
      <Link 
        to="/signin"
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-all duration-200 hover:scale-105"
      >
        <User className="w-4 h-4" />
        <span className="text-sm font-medium">Entrar</span>
      </Link>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 group"
      >
        <div className="relative">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-primary-foreground font-medium text-sm shadow-md">
            {initials}
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-background animate-pulse" />
        </div>
        
        <div className="hidden md:block">
          <div className="flex items-center gap-1">
            <span className="text-sm font-medium truncate max-w-[100px]">
              {user.email?.split('@')[0]}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </div>
          <span className="text-xs text-foreground/60">Online</span>
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="absolute right-0 mt-2 w-64 bg-card rounded-lg shadow-lg border border-border/10 overflow-hidden z-50"
            >
              <div className="p-4 border-b border-border/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-primary-foreground font-medium shadow-md">
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{user.email}</p>
                    <p className="text-xs text-foreground/60">Administrador</p>
                  </div>
                </div>
              </div>
              
              <div className="p-2">
                <Link
                  to="/profile"
                  className="flex items-center gap-2 w-full px-3 py-2 hover:bg-secondary/50 rounded-md transition-colors text-sm"
                  onClick={() => setIsOpen(false)}
                >
                  <UserCircle className="w-4 h-4" />
                  Ver perfil completo
                </Link>
                
                <button
                  onClick={handleSignOut}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 mt-3 bg-destructive text-destructive-foreground font-medium rounded-md transition-all duration-200 text-sm hover:bg-destructive/90 hover:scale-[1.02] shadow-md hover:shadow-lg border border-destructive/20"
                >
                  <LogOut className="w-5 h-5" />
                  Terminar sessão
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AuthStatus;